import React from "react";
import "animate.css";
import { useNavigate } from "react-router-dom";
import fishPlateImg from "../assets/images/fish-plate.jpg";

const Row = ({ name, sub, note, price, callNote }) => (
  <div className="py-2 border-b border-white/10 last:border-0">
    <div className="flex items-baseline gap-2">
      <span className="font-semibold text-white text-sm leading-snug flex-1 min-w-0">
        {name}
        {sub && (
          <span className="text-white/55 font-normal italic text-xs ml-1.5">
            {sub}
          </span>
        )}
      </span>
      {price && (
        <>
          <span className="shrink-0 border-b border-dotted border-white/25 flex-1 mx-2 self-end mb-1 hidden sm:block" />
          <span className="shrink-0 font-bold text-amber-400 text-sm whitespace-nowrap">
            {price}
          </span>
        </>
      )}
    </div>
    {note && (
      <p className="text-white/50 text-xs italic mt-0.5">
        {note}
      </p>
    )}
    {callNote && (
      <p className="text-amber-400/80 text-xs font-medium mt-0.5">
        {callNote}
      </p>
    )}
  </div>
);

const MenuCard = ({ icon, title, accent, children }) => (
  <div className="animate__animated animate__fadeInUp bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/15 flex flex-col">
    <div className={`px-5 py-4 ${accent}`}>
      <h3 className="text-base font-bold text-white tracking-wide flex items-center gap-2">
        <span className="text-lg">{icon}</span>
        {title}
      </h3>
    </div>
    <div className="px-5 py-3 flex-1">{children}</div>
  </div>
);

function MenuGridSection() {
  const navigate = useNavigate();

  return (
    <section
      id="menu-section"
      className="py-16 bg-cover bg-center"
      style={{
        backgroundImage: `url(${fishPlateImg})`,
        backgroundColor: "rgba(0,0,0,0.72)",
        backgroundBlendMode: "overlay",
      }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{
              fontFamily: "'Signika Negative', sans-serif",
            }}
          >
            MENU
          </h2>
          <div className="w-16 h-1 bg-amber-500 mx-auto mb-5 rounded-full" />
          <p className="text-lg text-white/75 max-w-2xl mx-auto">
            Take a look at the categories of foods and drinks we
            offer
          </p>
        </div>

        {/* Grid — 2 cols on md, 3 on lg */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Breakfast */}
          <MenuCard
            icon="🍳"
            title="Breakfast Plates"
            accent="bg-stone-700/80"
          >
            <div className="py-1.5 border-b border-white/10">
              <p className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-1">
                ⭐ Combo
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-white text-sm font-semibold flex-1">
                  Bacon, Ham or Turkey · Scrambled Eggs &amp;
                  Cheese on a Roll
                </span>
                <span className="text-amber-400 font-bold text-sm whitespace-nowrap">
                  $6.00
                </span>
              </div>
              <p className="text-white/50 text-xs italic">
                with Coffee or Tea
              </p>
            </div>
            <Row
              name="Spaghetti Haitien"
              price="$10.00"
              note="With hot dogs, onions, peppers &amp; light tomato sauce"
            />
            <Row
              name="Mayi Moulen"
              sub="Cornmeal"
              price="$10.00"
            />
          </MenuCard>

          {/* Lunch */}
          <MenuCard
            icon="☀️"
            title="Lunch Specials"
            accent="bg-amber-700/80"
          >
            <p className="text-amber-300 text-xs font-semibold tracking-wide mt-1 mb-3">
              Available 12 PM – 3 PM
            </p>
            <Row name="Chicken + Plantains" price="$10.00" />
            <Row
              name="Crispy Griot Bites + French Fries"
              price="$10.00"
            />
          </MenuCard>

          {/* Main Dishes */}
          <MenuCard
            icon="🍽️"
            title="Main Dishes"
            accent="bg-stone-700/80"
          >
            <div className="flex gap-2 flex-wrap mt-1 mb-3">
              <span className="text-xs bg-white/15 text-white px-2.5 py-0.5 rounded-full font-semibold">
                Medium $15
              </span>
              <span className="text-xs bg-white/15 text-white px-2.5 py-0.5 rounded-full font-semibold">
                Large $20
              </span>
            </div>
            <p className="text-white/45 text-xs italic mb-2">
              Served with Rice, Plantains &amp; Pikliz
            </p>
            <Row name="Poulet Frite / Poulet en Sauce / Jerk Chicken" />
            <Row name="Legim" sub="Vegetable Stew with Meat" />
            <Row
              name="Fritaille"
              sub="Fried meat, Plantains, Accra &amp; Pikliz"
              price="$25"
            />
            <Row name="Griot" sub="Fried Pork" />
            <Row name="Tassot Kabrit" sub="Fried Goat" />
            <Row name="Tassot Bef" sub="Fried Beef" />
            <Row
              name="Poisson"
              sub="Whole Fried Fish"
              price="$30"
              callNote="Call ahead to order"
            />
          </MenuCard>

          {/* Friday & Sunday */}
          <MenuCard
            icon="🍲"
            title="Daily Specials"
            accent="bg-red-900/80"
          >
            <div className="mt-1">
              <span className="inline-block text-xs font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full mb-1.5">
                Friday
              </span>
              <Row
                name="Ragout Beef"
                sub="Stewed cow foot"
                price="$12"
              />
              <Row name="Soup Bouillon" price="$10.00" />
            </div>
            <div className="mt-2">
              <span className="inline-block text-xs font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full mb-1.5">
                Diri Djon Djon
              </span>
              <Row name="Plain" price="$12.00" />
              <Row name="With Chicken" price="$15.00" />
              <Row name="With Stew Vegetables" price="$15.00" />
            </div>
            <div className="mt-2">
              <span className="inline-block text-xs font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full mb-1.5">
                Saturday
              </span>
              <Row name="Tonmtonm ak Sos Kalalou" price="$15" />
              <Row
                name="Bouillon"
                sub="Beef &amp; Vegetable Soup"
                price="$12"
              />
            </div>
            <div className="mt-2">
              <span className="inline-block text-xs font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full mb-1.5">
                Sunday
              </span>
              <Row
                name="Soup Joumou"
                sub="Haitian pumpkin soup with beef &amp; vegetables"
                price="$12"
              />
            </div>
            <div className="mt-2 border-t border-white/10 pt-2">
              <Row
                name="Fried Chicken / Baked Turkey Wings"
                note="With Collard Greens &amp; Mac &amp; Cheese"
                price="$20"
              />
            </div>
          </MenuCard>

          {/* Drinks */}
          <MenuCard
            icon="🥤"
            title="Drinks"
            accent="bg-stone-700/80"
          >
            <Row name="Coffee / Tea" price="$2.00" />
            <Row name="Bottle of Water" price="$1.00" />
            <Row name="Ginger Drink" price="$5.00" />
            <Row name="Acassan Drink" price="$5.00" />
            <Row
              name="Fresh Juice"
              price="$4.00"
              note="Lemonade, Carrot, Beet Juice, Passion Fruit"
            />
          </MenuCard>

          {/* Patties */}
          <MenuCard
            icon="🥟"
            title="Haitian Patties"
            accent="bg-stone-700/80"
          >
            <Row name="Haitian Patties" price="$2.00" />
          </MenuCard>

          {/* CTA card */}
          <div className="animate__animated animate__fadeInUp bg-amber-700/90 rounded-2xl border border-amber-500/40 flex flex-col items-center justify-center p-8 text-center gap-4">
            <p className="text-white/80 text-sm">
              Want the full menu with all details?
            </p>
            <button
              onClick={() => navigate("/menu")}
              className="bg-white text-amber-800 font-bold px-6 py-3 rounded-full hover:bg-amber-50 transition-colors duration-200 text-sm shadow-lg"
            >
              View Full Menu →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MenuGridSection;
